/**
 * Contact-flow helpers shared by the desktop Contact button and the mobile
 * lead card, kept here as ONE source of truth so the two surfaces cannot drift.
 * Pure functions — no React, no Redux, no API calls.
 */

/**
 * Resolves whether the Contact popup should open on the WhatsApp tab for a lead.
 * Extracted verbatim from the desktop Contact button (CotactCell.jsx) so the
 * mobile lead card (NewLeadDataGrid) and the desktop button evaluate the WhatsApp
 * preference identically.
 *
 * @param {object} row - lead row (V1 or flat shape)
 * @returns {boolean} true → open Contact popup on the WhatsApp tab
 */
export function resolveWhatsappPreferred(row) {
    const isFirstContactWhatsappReferral =
        row?.referral === 'WHATSAPP' &&
        row?.status !== 'FAILED_TO_ATTEND_CONSULTATION' &&
        row?.lead_treatments__lead_contacted_counts__count === 0;
    const prefersWhatsapp =
        row?.originalData?.lead_contact?.preferred_contact_method?.toLowerCase() === 'whatsapp';
    return Boolean(isFirstContactWhatsappReferral || prefersWhatsapp);
}

/**
 * Factory for the mobile lead-card "Contact Lead" CTA handler. Routes through
 * handlePopup — the SAME task/checklist-aware Contact flow the desktop Contact
 * button uses (→ selectedRow('contactLead', ...)) — instead of the old generic
 * followUpLead popup. The card is a WHOLE-LEAD CTA, so it carries no single
 * v2TaskId (only per-checklist-cell actions do); handlePopup passes none either,
 * matching desktop parity.
 *
 * @param {(id:any, status:any, row:object, openWhatsapp:boolean) => void} handlePopup
 * @returns {(row:object) => void}
 */
export function makeMobileContactHandler(handlePopup) {
    return (row) => {
        // Resolve the lead id robustly: most table rows carry lead_id, but some
        // flat/archive/composite mobile-card shapes key by id (or leadId/lead).
        const leadId = row?.lead_id ?? row?.leadId ?? row?.lead ?? row?.id;
        return handlePopup(leadId, row?.status, row, resolveWhatsappPreferred(row));
    };
}
